import { BrowserModule, Title } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ErrorHandler, NgModule } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { AgGridModule } from "ag-grid-angular";
import { CommonModule } from "@angular/common";
import { ModalModule } from "ngx-bootstrap/modal";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from "@azure/msal-browser";
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import { environment } from "src/environments/environment";

import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatSidenavModule } from "@angular/material/sidenav";

import { DragDropModule } from "@angular/cdk/drag-drop";
import { App } from "./app";
import { Home } from "./home/home";

import {
  ErrorService,
  TimeAgoPipe,
  Pager,
  SearchBox,
  SearchBoxDlg,
  FilterDisplay,
  ListView,
  ListRenderItems,
  ListSearchBox,
  NotFoundView,
  HelpView,
  PersonLink,
  RouterLinkRenderer,
  CheckboxRenderer,
  CheckboxEditor,
  BackButtonDirective,
  DeleteRowRenderer,
  AddRowHeader,
  GridListView,
} from "./common";

import { SecurityListView, SecurityEditorView, SecurityDetailView } from "./security";
import {
  ManifestListView,
  ManifestPropertyEditorView,
  ManifestDetailView,
  ManifestSearchBox,
  ManifestFilters,
} from "./manifest";
import {
  ContainerDetailView,
  ContainerEdit,
  ContainerListView,
  ContainerSearchBox,
  ContainersFilters,
} from "./container";
import { ItemDetail, ItemEdit, ItemListView, ItemSearchBox, ItemsFilters } from "./item";

import { PeoplePicker, PeopleSearchBox } from "./people";
import { DatepickerEditor } from "./common/grid/datepicker-editor";
import { SERVICE_PROVIDERS } from "./common/services/providers";

import { PipeFilter } from "./common/input-filter/pipe";
import { InputFilter } from "./common/input-filter/input-filter.component";
import { canDeactivateGuard } from "./can-deactivate-guard.service";
import {
  ContainerTypesView,
  FacilitiesView,
  FacilityTypesView,
  FormCodesView,
  ItemContainerMaterialsView,
  ManagementCodesView,
  ProcessCategoriesView,
  SourceCodesView,
  TsdfCodesView,
  UnitsView,
} from "./auxiliary";
import { TransportersView } from "./auxiliary/transporters-view";
import { WasteStreamView } from "./auxiliary/waste-stream-view";

const isIE = environment.isIE;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: `https://login.microsoftonline.com/${environment.tenantId}/`,
      redirectUri: environment.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap: Map<string, Array<string>> = new Map([
    ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
    [`${environment.wimsApi}**`, [environment.apiScope]],
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}

@NgModule({
  declarations: [
    App,
    Home,
    PipeFilter,
    TimeAgoPipe,
    Pager,
    SearchBox,
    SearchBoxDlg,
    FilterDisplay,
    ListView,
    GridListView,
    PersonLink,
    ListRenderItems,
    ListSearchBox,
    NotFoundView,
    HelpView,
    RouterLinkRenderer,
    DeleteRowRenderer,
    AddRowHeader,
    DatepickerEditor,
    InputFilter,
    BackButtonDirective,
    CheckboxRenderer,
    CheckboxEditor,
    SecurityListView,
    SecurityEditorView,
    SecurityDetailView,
    PeoplePicker,
    PeopleSearchBox,
    ManifestListView,
    ManifestDetailView,
    ManifestPropertyEditorView,
    ManifestSearchBox,
    ManifestFilters,
    ContainerEdit,
    ContainerDetailView,
    ContainerListView,
    ContainersFilters,
    ContainerSearchBox,
    ItemEdit,
    ItemDetail,
    ItemListView,
    ItemsFilters,
    ItemSearchBox,
    FacilityTypesView,
    SourceCodesView,
    FormCodesView,
    UnitsView,
    ProcessCategoriesView,
    ItemContainerMaterialsView,
    ContainerTypesView,
    TsdfCodesView,
    FacilitiesView,
    WasteStreamView,
    TransportersView,
    ManagementCodesView,
  ],
  imports: [
    BrowserAnimationsModule,
    AgGridModule,
    BsDatepickerModule.forRoot(),
    BsDropdownModule.forRoot(),
    BrowserModule,
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    DragDropModule,
    CommonModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    ModalModule.forRoot(),
    MsalModule,
  ],
  providers: [
    PipeFilter,
    canDeactivateGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: ErrorHandler, useClass: ErrorService },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    Title,
    ...SERVICE_PROVIDERS,
  ],
  bootstrap: [App],
})
export class AppModule {}
